import { defineStore } from 'pinia'
import { RESOURCE_NAMES,
	readResourceItem, updateResourceItem, readResources,
	updatePostMetrics,
} from '@/api/api'
import { Post } from '@/models/post.js'


const _passportRequestArguments = [
	`embedded={
		"${Post.passportKeys.orgId}":1,
		"${Post.passportKeys.creatorId}":1,
		"${Post.passportKeys.updateBy}":1,
		"${Post.passportKeys.pipelineId}":1
	}`
]

export const usePostsStore = defineStore('postsStore', {
    state: () => ({
		posts: new Map(),
		// Organization ID for filtering post
		organizationId: null,
		// Departments ID for filtering post
		departmentsId: [],
		// Sectors ID for filtering post
		sectorsId: [],
		// Access keys for rest API (mongoDB)
		_api: {
			login: null,
			password: null,
		},
	}),

    getters: {
        isNoPosts() { return this.posts.size === 0 },

		postsArray() { return Array.from(this.posts.values()) },
    },

    actions: {
		setOrganizationId(orgId=null) {
			this.organizationId = orgId
		},

		setDepartmnetsId(id=[]) {
			this.departmentsId = id
		},

		setSectorsId(id=[]) {
			this.sectorsId = id
		},


		setApiAccessKeys(login, password) {
			this._api.login = login
			this._api.password = password
		},

		post(id) { return this.posts.get(id); },

		createPost(passport, settings) {
			console.log(`Create post. Passport ${passport},\nsettings ${settings}`)
		},

		// Read all posts passports to component posts Map
		async readPosts(handler) {
			//console.log("_passportRequestArguments = ", _passportRequestArguments)
			let args = [ ... _passportRequestArguments ]
			let departments = await this._readOrgDepartments()
			
			let filters = []
			// If organization id is set
			if (this.organizationId !== null) {
				// Add filter
				filters.push(`"${Post.passportKeys.orgId}": "${this.organizationId}"`)				
			}

			// If user departments is set
			if (this.departmentsId.length > 0) {
				// Add filters
				// Example:
				// 	"$or": [
				// 		{"department_id": {"$in": ["***", "***"]}}, // add posts with department_id == ***
				// 		{"department_id": null} // add posts witout department_id
				// 	],
				let filter = `"$or":[{"${Post.passportKeys.departmentId}": {"$in": [`
				for (const id of this.departmentsId) {
					filter += `"${id}",`
				}
				filter = filter.slice(0, -1); 
				filter += ']}},'
				filter += `{"${Post.passportKeys.departmentId}": null}]`
				filters.push(filter)
			}

			// If user sectors is set
			if (this.sectorsId.length > 0) {
				// Add filters
				let filter = `"$or":[{"${Post.passportKeys.sectorId}": {"$in": [`
				for (const id of this.sectorsId) {
					filter += `"${id}",`
				}
				filter = filter.slice(0, -1); 
				filter += ']}},'
				filter += `{"${Post.passportKeys.sectorId}": null}]`
				filters.push(filter)
			}

			// If any filters is set
			if (filters.length > 0) {
				// Add all filters to request arguments
				let arg = 'where={'
				for (const filter of filters) {
					arg += `${filter},`
				}
				arg = arg.slice(0, -1); 
				arg += '}'
				
				args.push(arg)
			}

			const passports = await readResources(
				RESOURCE_NAMES.postPassports,
				this._api.login,
				this._api.password,
				args
			)

			if (passports.length == 0) {
				if (handler) { handler(true) }
				return
			}

			this.posts.clear()

			passports.forEach(passport => {
				const post = new Post(passport._id)

				this._updatePostPassportInStorage(post, passport, departments)

				// Save post Object
				this.posts.set(passport._id, post)
			})

			if (handler) { handler(true) }
		},

		/* Read metrics and save to state this.posts
		* handler: function calls after fetch (result:boolean) => {}
		* postId: if present - read only post with postId
		*/
		async readMetrics(handler, postId) {
			let posts = undefined
			let updateMetricsResult = false

			if (postId) {
				if (!this.posts.has(postId)) {
					await this.readPosts()
				}

				posts = new Map([[postId, this.posts.get(postId)]])
			} else {
				posts = this.posts
			}

			await updatePostMetrics(posts)
			.then(result => {
				updateMetricsResult = result
			}).catch(err => {
				console.error("Error on read posts metrics: ", err, ".")
				if (handler) { handler(false) }
			})
			if (!updateMetricsResult) {
				return
			}


			if (handler) {
				handler(updateMetricsResult)
			}
		},


		/* Read passport and save to state this.posts
		* handler: function calls after fetch (result:boolean) => {}
		* postId: if present - read only post with postId
		*/
		async readPassport(handler, postId) {
			if (postId) {
				let departments = await this._readOrgDepartments()

				// Read one post passport
				readResourceItem(
					RESOURCE_NAMES.postPassports,
					postId,
					this._api.login,
					this._api.password,
					_passportRequestArguments
				)
				.then(response => {
					//console.log("response", response)
					let post = this.posts.get(response._id)
					if (!post) {
						post = new Post(response._id)
					}

					//post.passport = new Map(Object.entries(response))
					this._updatePostPassportInStorage(post, response, departments)

					// If no post in storage
					if (!this.posts.has(response._id)) {
						// Save post Object
						this.posts.set(response._id, post)
					}

					if (handler) {
						handler(true)
					}
				}).catch(error => {
					console.error(`Error on read post passport: "${error}".`)
					if (handler) {
						handler(false)
					}
				})
			} else {
				// Read passports for all posts
			}
		},

		/* Update passport and save to state this.posts
		* handler: function calls after fetch (result:boolean) => {}
		* postId: update post with postId
		* payload: Object updated passport fields
		*/
		updatePasport(handler, postId, payload) {
			// Update one post passport
			updateResourceItem(
				RESOURCE_NAMES.postPassports,
				this._api.login,
				this._api.password,
				this.post(postId).passport.get("_id"),
				this.post(postId).passport.get("_etag"),
				payload
			).then(result => {
				if (result._status == 'OK') {
					//console.log("Паспорт записано успіно.")
					this.post(postId).passport.set("_etag", result._etag)
					this.post(postId).passport.set("_updated", result._updated)

					// Update 
					for (const [key, value] of Object.entries(payload)) {
						this.post(postId).passport.set(key, value)
					}

					if (handler) {
						handler(true)
					}
				} else {
					if (handler) {
						handler(false)
					}
				}
			}).catch(err => {
				console.error("on update post passport", err)
				if (handler) {
					handler(false)
				}
			})
		},

		// delete ?

		// Reads station settings history. Do not save history to store!
		// Return Array of passport Objects or undefined
		async postPasportHistory(postId) {
			let args = [ ... _passportRequestArguments, 'version=all' ]

			return await readResourceItem(
				RESOURCE_NAMES.postPassports,
				postId,
				this._api.login,
				this._api.password,
				args,
			).then(response => {
				return response._items
			}).catch((error) => {
				switch (error.cause?.status) {
					case 500:
						break
					case undefined:
						console.error(`Error "${error.message}" on post ${postId} version history request`)
						break
					default:
						console.error(`Error on post ${postId} version history request`)
						break
				}

				return []
			})
		},

		/*
		Reads current organization departments or all organizations departments
		depends on this.organizationId.
		Returns Map  orgId: departments_object
		*/
		async _readOrgDepartments() {
			let departments = new Map() // orgId: departments
			
			// If organization id is set
			if (this.organizationId !== null) {
				// Read organization
				const result = await readResourceItem(
					RESOURCE_NAMES.organizations,
					this.organizationId,
					this._api.login,
					this._api.password,
				).then(response => {
					return response
				}).catch((err) => {
					console.error("on read organization departments", err)
					return null
				})

				if (result) {
					departments.set(result._id, result.departments)
				}
			} else {
				// Read all organizations
				const result = await readResources(
					RESOURCE_NAMES.organizations,
					this._api.login,
					this._api.password,
				)

				result.forEach((org) => {
					departments.set(org._id, org.departments)
				})
			}

			return departments
		},


		/*
		Updates post object with new passport
		*/
		async _updatePostPassportInStorage(post, passport, departments) {
			// Write passport fields to post passport
			Object.entries(passport).forEach(([key, value]) => {
				if (key == Post.passportKeys.orgId) {
					post.passport.set(Post.passportKeys.org, value)
					post.passport.set(Post.passportKeys.orgId, value._id)
					// Add organization name to passport (copy from organization object)
					post.passport.set(Post.passportKeys.orgName, value.title)
				} else {
					post.passport.set(key, value)
				}
			})

			// Add department object to post
			const orgId = post.passport.get(Post.passportKeys.orgId)
			const orgDeps = departments.get(orgId)
			let curPostDepartment = undefined
			// If all posts in same organization
			if (orgDeps) {
				curPostDepartment = orgDeps.find(
					dep => dep.id == post.passport.get(Post.passportKeys.departmentId)
				)

				post.passport.set(
					Post.passportKeys.department,
					curPostDepartment
				)
			}

			// Add sector object to post
			const curPostSectorId = post.passport.get(Post.passportKeys.sectorId)
			if (curPostSectorId && curPostDepartment && curPostDepartment.sectors) {
				const curPostSector = curPostDepartment.sectors.find(
					sec => sec.id == curPostSectorId
				)
				post.passport.set(
					Post.passportKeys.sector,
					curPostSector
				)
			}
		},
    },
})
